import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { CircularProgress, Tooltip } from "@mui/material";
import moment from "moment";
import DocumentRepository, { DocumentInfo } from "../../Repositories/DocumentRepository";
import TransportSubscriptionRepository, { TransportSubscriptionInfo } from "../../Repositories/TransportSubscriptionRepository";
import TeachrRepository from "../../Repositories/TeachrRepository";
import TransportSubscriptionUpload, { SubscriptionClientOption } from "../DeclarationCourse/Travel/TransportSubscriptionUpload";
import { resolveClientRowFields } from "../Clients/clientDetailNavigation";
import { isDepositEnabled } from "../../config/environment.config";
import { DV2 } from "../DeclarationV2/theme";
import { plural } from "../DeclarationV2/format";
import { vocab } from "../DeclarationV2/vocabulary";
import { Badge, Button, Card, TextLink, fieldStyle, useDv2Theme } from "../DeclarationV2/layout/Ui";
import {
    DOCUMENTS_TAB_PARAM, DOCUMENT_CATEGORIES, DocumentPeriod, categoryOf, dayHeading, documentBadge, documentDateLabel,
    documentDay, documentParentLine, documentPersonName, documentPill, documentSubline, documentTitle, isDocumentCategory, normalize,
    periodBoundsOf,
} from "./documentRows";

const GRID = "minmax(0,3fr) minmax(0,1.3fr) minmax(0,1.1fr) minmax(0,1.2fr) 40px";
const PAGE_SIZES = [10, 25, 50];
const PERIODS: Array<{ key: DocumentPeriod; label: string }> = [
    { key: "all", label: "Tout" },
    { key: "month", label: "Ce mois" },
    { key: "quarter", label: "3 derniers mois" },
    { key: "year", label: moment().format("YYYY") },
    { key: "custom", label: "Dates précises" },
];

/** Catégorie demandée par le state de navigation (notification d'abonnement : « transport »). */
const requestedCategoryOf = (state: unknown): string | null => {
    const value = (state as { documentsCategory?: unknown } | null)?.documentsCategory;
    return typeof value === "string" && isDocumentCategory(value) ? value : null;
};

interface ClientOption { id: number; name: string }

/**
 * « Mes documents » du prof repensé (maquette 9a) : mêmes documents, mêmes
 * onglets, mêmes filtres et mêmes statuts que `DocumentSpace`, avec le nom
 * du document en titre et son numéro en sous-ligne, un repère de catégorie à
 * gauche, les lignes groupées par jour et une carte des abonnements de
 * transport en cours. Le dépôt et la confirmation d'un abonnement passent
 * par les mêmes composants et appels qu'aujourd'hui.
 */
interface DocumentsV2Props {
    /** Prof connecté : ses documents, ses abonnements de transport, filtre par élève. */
    teachrId?: number;
    /** Client connecté (espace client repensé) : ses documents seulement, sans abonnement ni filtre par élève. */
    clientId?: number;
}

export default function DocumentsV2({ teachrId, clientId: ownerClientId }: DocumentsV2Props) {
    const { primary, fontFamily, text } = useDv2Theme();
    const isClient = ownerClientId != null;
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const [loading, setLoading] = useState(true);
    const [documents, setDocuments] = useState<DocumentInfo[]>([]);
    const [subscriptions, setSubscriptions] = useState<TransportSubscriptionInfo[]>([]);
    const [refresh, setRefresh] = useState(0);
    const [search, setSearch] = useState("");
    const [clientId, setClientId] = useState<number | "">("");
    const [period, setPeriod] = useState<DocumentPeriod>("all");
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [uploadClients, setUploadClients] = useState<SubscriptionClientOption[]>([]);
    const [confirmingId, setConfirmingId] = useState<number | null>(null);

    // Onglet : l'URL, sinon la catégorie demandée par une notification, sinon « Tous ».
    const requested = requestedCategoryOf(location.state);
    const paramTab = searchParams.get(DOCUMENTS_TAB_PARAM);
    const tab = isDocumentCategory(paramTab) ? (paramTab as string) : (requested ?? "all");
    const selectTab = (next: string) => {
        const params = new URLSearchParams(searchParams);
        if (next === "all") params.delete(DOCUMENTS_TAB_PARAM);
        else params.set(DOCUMENTS_TAB_PARAM, next);
        setSearchParams(params, { replace: true });
        setPage(0);
    };
    // Notification cliquée alors que la page est déjà ouverte : l'onglet suit.
    const [syncedKey, setSyncedKey] = useState(location.key);
    if (location.key !== syncedKey) {
        setSyncedKey(location.key);
        if (requested && paramTab !== requested) {
            const params = new URLSearchParams(searchParams);
            params.set(DOCUMENTS_TAB_PARAM, requested);
            setSearchParams(params, { replace: true });
        }
    }

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        if (isClient) {
            DocumentRepository.getClientDocuments(ownerClientId as number)
                .then((docs) => { if (!cancelled) setDocuments(docs?.documents ?? []); })
                .catch(() => { if (!cancelled) setDocuments([]); })
                .finally(() => { if (!cancelled) setLoading(false); });
            return () => { cancelled = true; };
        }
        Promise.allSettled([
            DocumentRepository.getTeachrDocuments(teachrId as number),
            TransportSubscriptionRepository.list(teachrId as number),
        ]).then(([docs, subs]) => {
            if (cancelled) return;
            setDocuments(docs.status === "fulfilled" ? (docs.value?.documents ?? []) : []);
            setSubscriptions(subs.status === "fulfilled" ? (subs.value?.subscriptions ?? (subs.value?.current ? [subs.value.current] : [])) : []);
            setLoading(false);
        });
        return () => { cancelled = true; };
    }, [teachrId, ownerClientId, isClient, refresh]);

    // ── Dépôt d'un abonnement (même composant qu'aujourd'hui) ───────────────
    const openUpload = async () => {
        if (isClient || !teachrId) return;
        setUploadOpen(true);
        if (uploadClients.length > 0) return;
        try {
            const response: any = await TeachrRepository.getAllClientsForTeachr({ teachrId, archive: 0, page: 1, variable: 0, sort: 1, limit: 500, validated: true } as any);
            const rows: any[] = response?.data ?? [];
            const options = rows
                .map((row) => { const { clientId: id, fullName } = resolveClientRowFields(row); return id ? { id: Number(id), name: fullName } : null; })
                .filter((o): o is SubscriptionClientOption => o !== null);
            setUploadClients(Array.from(new Map(options.map((o) => [o.id, o])).values()).sort((a, b) => a.name.localeCompare(b.name, "fr")));
        } catch (e) {
            console.warn("Liste des clients indisponible pour le dépôt d'abonnement", e);
        }
    };
    const confirmSubscription = async (doc: DocumentInfo) => {
        if (doc.subscriptionId == null) return;
        setConfirmingId(doc.subscriptionId);
        try {
            await TransportSubscriptionRepository.confirm(doc.subscriptionId);
            setRefresh((v) => v + 1);
        } catch (e) {
            console.warn("Confirmation de l'abonnement impossible", e);
        } finally {
            setConfirmingId(null);
        }
    };

    // ── Filtres (mêmes règles que DocumentSpace) ────────────────────────────
    const scoped = useMemo(
        () => documents
            .filter((doc) => isDepositEnabled() || doc.type !== "deposit_attestation")
            .filter((doc) => clientId === "" || doc.clientId === clientId),
        [documents, clientId],
    );
    const clientOptions = useMemo<ClientOption[]>(() => {
        const byId = new Map<number, ClientOption>();
        documents.forEach((doc) => {
            if (doc.clientId == null) return;
            const name = documentPersonName(doc) ?? doc.clientName ?? "";
            if (!byId.has(doc.clientId)) byId.set(doc.clientId, { id: doc.clientId, name });
        });
        return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name, "fr"));
    }, [documents]);
    const counts = useMemo(() => {
        const result: Record<string, number> = { all: scoped.length };
        DOCUMENT_CATEGORIES.forEach((c) => { result[c.key] = scoped.filter((d) => categoryOf(d) === c.key).length; });
        return result;
    }, [scoped]);
    const visibleTabs = [{ key: "all", label: "Tous" }, ...DOCUMENT_CATEGORIES.filter((c) => counts[c.key] > 0)];
    const activeTab = tab === "all" || counts[tab] > 0 || loading ? tab : "all";

    const filtered = useMemo(() => {
        const q = normalize(search.trim());
        const bounds = period === "custom"
            ? { from: from ? moment(from, "YYYY-MM-DD").startOf("day") : null, to: to ? moment(to, "YYYY-MM-DD").endOf("day") : null }
            : periodBoundsOf(period);
        return scoped
            .filter((doc) => {
                if (activeTab !== "all" && categoryOf(doc) !== activeTab) return false;
                if (q && !normalize(`${doc.label} ${doc.number ?? ""} ${doc.clientName ?? ""} ${doc.childName ?? ""}`).includes(q)) return false;
                const start = doc.validFrom ?? doc.date;
                const end = doc.validUntil ? moment(doc.validUntil, "YYYY-MM-DD").endOf("day") : (doc.date ? moment(doc.date) : null);
                if (bounds.from && (!end || end.isBefore(bounds.from))) return false;
                if (bounds.to && (!start || moment(start).isAfter(bounds.to))) return false;
                return true;
            })
            .sort((a, b) => (documentDay(b)?.valueOf() ?? 0) - (documentDay(a)?.valueOf() ?? 0));
    }, [scoped, activeTab, search, period, from, to]);

    const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
    const safePage = Math.min(page, pageCount - 1);
    const pageDocs = filtered.slice(safePage * pageSize, safePage * pageSize + pageSize);
    const groups = useMemo(() => {
        const out: Array<{ key: string; heading: string; docs: DocumentInfo[] }> = [];
        pageDocs.forEach((doc) => {
            const d = documentDay(doc);
            const key = d ? d.format("YYYY-MM-DD") : "unknown";
            const last = out[out.length - 1];
            if (last && last.key === key) last.docs.push(doc);
            else out.push({ key, heading: d ? dayHeading(d) : "Date inconnue", docs: [doc] });
        });
        return out;
    }, [pageDocs]);
    const hasFilters = !!(search || clientId !== "" || period !== "all");
    const clearFilters = () => { setSearch(""); setClientId(""); setPeriod("all"); setFrom(""); setTo(""); setPage(0); };
    const pages = (): Array<number | "…"> => {
        if (pageCount <= 6) return Array.from({ length: pageCount }, (_, i) => i);
        const set = new Set<number>([0, 1, 2, safePage - 1, safePage, safePage + 1, pageCount - 1].filter((p) => p >= 0 && p < pageCount));
        const sorted = Array.from(set).sort((a, b) => a - b);
        const result: Array<number | "…"> = [];
        sorted.forEach((p, i) => { if (i > 0 && p - (sorted[i - 1] as number) > 1) result.push("…"); result.push(p); });
        return result;
    };

    // ── Abonnements en cours ────────────────────────────────────────────────
    const activeSubscriptions = subscriptions.filter((s) => !s.isExpired);
    const subscriptionLine = (s: TransportSubscriptionInfo) => {
        const until = moment(s.validUntil).format("D MMMM YYYY");
        const soon = s.expiresInDays <= 7 ? `, expire dans ${plural(Math.max(0, s.expiresInDays), "jour")}` : "";
        return `${s.typeLabel}${s.clientName ? ` pour ${s.clientName}` : ""}, valide jusqu'au ${until}${soon}${s.needsConfirmation ? ", à confirmer" : ""}`;
    };

    const pillButton = (active: boolean): React.CSSProperties => ({
        fontFamily, fontSize: 13, fontWeight: 700, padding: "8px 14px", borderRadius: 999, border: "none", cursor: "pointer",
        background: active ? primary : DV2.tint, color: active ? "#fff" : primary,
    });
    const iconButton: React.CSSProperties = { width: 34, height: 34, borderRadius: 10, border: `1px solid ${DV2.border}`, background: "#fff", color: primary, display: "grid", placeItems: "center", cursor: "pointer", fontWeight: 800, fontSize: 15, fontFamily };

    const subtitle = loading
        ? "Chargement de vos documents…"
        : isClient
            ? `${plural(documents.length, "document")}, devis, factures et attestations fiscales`
            : `${plural(documents.length, "document")}, devis, factures, attestations et abonnements de transport`;

    return (
        <div className="mx-auto w-full /max-w-6xl px-4 sm:px-6 py-4 lg:py-8 flex flex-col gap-5" style={{ fontFamily, color: text }}>
            <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <h1 style={{ fontSize: 26, fontWeight: 800, margin: 0, lineHeight: 1.2 }}>Mes documents</h1>
                    <div style={{ fontSize: 14, color: DV2.muted, marginTop: 4 }}>{subtitle}</div>
                </div>
                {!isClient && <Button label="Déposer un abonnement de transport" variant="secondary" onClick={openUpload} />}
            </header>

            {uploadOpen && teachrId && (
                <TransportSubscriptionUpload
                    open
                    teachr={{ id: teachrId } as any}
                    clientOptions={uploadClients}
                    onClose={() => setUploadOpen(false)}
                    onSaved={() => { setUploadOpen(false); setRefresh((v) => v + 1); }}
                />
            )}

            {/* Abonnements de transport en cours (prof seulement) */}
            {!loading && !isClient && (
                <Card padding={18} style={{ display: "flex", gap: 16, alignItems: "flex-start" }} tint={activeSubscriptions.length === 0}>
                    <div aria-hidden style={{ width: 40, height: 44, borderRadius: 8, background: activeSubscriptions.length > 0 ? DV2.tint : "#fff", color: primary, display: "grid", placeItems: "center", fontSize: 10, fontWeight: 800, flexShrink: 0 }}>ABO</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 800, fontSize: 15 }}>
                            {activeSubscriptions.length > 0 ? `${plural(activeSubscriptions.length, "abonnement")} de transport en cours` : "Aucun abonnement de transport en cours"}
                        </div>
                        {activeSubscriptions.length > 0 ? (
                            <ul style={{ margin: "4px 0 0", padding: 0, listStyle: "none", fontSize: 13, color: DV2.textSoft, lineHeight: 1.6 }}>
                                {activeSubscriptions.map((s) => <li key={s.id}>{subscriptionLine(s)}.</li>)}
                            </ul>
                        ) : null}
                        <div style={{ fontSize: 13, color: DV2.muted, marginTop: 4 }}>
                            {activeSubscriptions.length > 0
                                ? `Il sert de preuve de déplacement pour vos ${vocab.courses()} avec ${activeSubscriptions.length > 1 ? "ces" : "cet"} ${activeSubscriptions.length > 1 ? vocab.clients() : vocab.client()}, un abonnement par ${vocab.client()}.`
                                : `Déposez votre abonnement (Navigo, TER…) pour un ${vocab.client()} : il servira de preuve de déplacement pour vos ${vocab.courses()} avec lui tant qu'il est valide.`}
                        </div>
                    </div>
                    {counts.transport > 0 && <TextLink label="Voir" onClick={() => selectTab("transport")} />}
                </Card>
            )}

            {/* Onglets */}
            <div role="tablist" aria-label="Catégories de documents" className="flex gap-1 overflow-x-auto" style={{ borderBottom: `1px solid ${DV2.border}` }}>
                {visibleTabs.map((t) => {
                    const selected = t.key === activeTab;
                    return (
                        <button key={t.key} type="button" role="tab" aria-selected={selected} onClick={() => selectTab(t.key)} style={{ fontFamily, fontSize: 15, fontWeight: 800, padding: "10px 16px", background: "none", border: "none", borderBottom: `3px solid ${selected ? primary : "transparent"}`, color: selected ? primary : DV2.muted, cursor: "pointer", marginBottom: -1, whiteSpace: "nowrap" }}>
                            {t.label}{loading ? "" : `, ${counts[t.key] ?? 0}`}
                        </button>
                    );
                })}
            </div>

            <Card padding={0} style={{ overflow: "hidden" }}>
                {/* Filtres */}
                <div className="flex flex-col lg:flex-row lg:items-center gap-3" style={{ padding: "14px 20px", borderBottom: `1px solid ${DV2.borderSoft}` }}>
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                        placeholder={isClient ? "Numéro ou document" : `Numéro, ${vocab.client()} ou document`}
                        aria-label="Rechercher un document"
                        style={{ ...fieldStyle(), background: DV2.pageBg, fontWeight: 500, fontSize: 14, maxWidth: 380 }}
                    />
                    <div className="flex flex-wrap items-center gap-2 lg:ml-auto">
                        {!isClient && clientOptions.length > 0 && (
                            <select
                                value={clientId}
                                onChange={(e) => { setClientId(e.target.value === "" ? "" : Number(e.target.value)); setPage(0); }}
                                aria-label={`Filtrer par ${vocab.client()}`}
                                style={{ ...fieldStyle(), width: "auto", fontSize: 13, fontWeight: 700, padding: "8px 12px", fontFamily, appearance: "auto" as any }}
                            >
                                <option value="">Tous les {vocab.clients()}</option>
                                {clientOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        )}
                        <div role="group" aria-label="Période" className="flex flex-wrap gap-2">
                            {PERIODS.map((p) => (
                                <button key={p.key} type="button" aria-pressed={period === p.key} style={pillButton(period === p.key)} onClick={() => { setPeriod(p.key); setPage(0); }}>{p.label}</button>
                            ))}
                        </div>
                        {hasFilters && <TextLink label="Effacer les filtres" onClick={clearFilters} />}
                    </div>
                </div>
                {period === "custom" && (
                    <div className="flex flex-wrap items-center gap-3" style={{ padding: "10px 20px", borderBottom: `1px solid ${DV2.borderSoft}`, fontSize: 13 }}>
                        <label className="flex items-center gap-2">Du <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(0); }} style={{ ...fieldStyle(), width: "auto", padding: "6px 10px", fontSize: 13 }} /></label>
                        <label className="flex items-center gap-2">au <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(0); }} style={{ ...fieldStyle(), width: "auto", padding: "6px 10px", fontSize: 13 }} /></label>
                    </div>
                )}

                {/* En-tête */}
                <div className="hidden md:grid" style={{ gridTemplateColumns: GRID, padding: "10px 20px", fontSize: 11, fontWeight: 800, color: DV2.placeholder, textTransform: "uppercase", letterSpacing: ".06em", borderBottom: `1px solid ${DV2.borderSoft}`, gap: 12 }}>
                    <span>Document</span><span>{isClient ? "Pour" : vocab.client()}</span><span>Date</span><span>Statut</span><span />
                </div>

                {loading ? (
                    <div style={{ padding: 32, textAlign: "center", color: DV2.muted, fontSize: 14 }}><CircularProgress size={18} style={{ marginRight: 8, verticalAlign: "middle" }} /> Chargement de vos documents…</div>
                ) : filtered.length === 0 ? (
                    <div style={{ padding: "48px 20px", textAlign: "center", color: DV2.muted, fontSize: 14 }}>
                        <div style={{ fontSize: 28 }} aria-hidden>📁</div>
                        <div style={{ marginTop: 8 }}>{documents.length === 0 ? "Aucun document pour le moment. Vos devis, factures et attestations apparaîtront ici au fil de vos déclarations." : "Aucun document ne correspond aux filtres choisis."}</div>
                        {hasFilters && <div style={{ marginTop: 8 }}><TextLink label="Effacer les filtres" onClick={clearFilters} /></div>}
                    </div>
                ) : groups.map((group) => (
                    <section key={group.key} aria-label={group.heading}>
                        <div style={{ padding: "8px 20px", fontSize: 11, fontWeight: 800, color: DV2.placeholder, textTransform: "uppercase", letterSpacing: ".06em", background: "#F9FBFD", borderBottom: `1px solid ${DV2.borderSoft}` }}>{group.heading}</div>
                        {group.docs.map((doc, i) => {
                            const pill = documentPill(doc);
                            const person = documentPersonName(doc);
                            const parent = documentParentLine(doc);
                            const title = documentTitle(doc);
                            const sub = documentSubline(doc);
                            const canConfirm = doc.type === "transport" && doc.status === "to_confirm" && doc.subscriptionId != null;
                            const statusCell = (
                                <div className="flex flex-wrap items-center gap-2">
                                    {pill && (pill.tooltip ? <Tooltip title={pill.tooltip}><span><Badge tone={pill.tone}>{pill.label}</Badge></span></Tooltip> : <Badge tone={pill.tone}>{pill.label}</Badge>)}
                                    {canConfirm && <Button label="Confirmer" size="sm" variant="secondary" loading={confirmingId === doc.subscriptionId} disabled={confirmingId !== null} onClick={() => confirmSubscription(doc)} />}
                                </div>
                            );
                            const download = (
                                <Tooltip title="Ouvrir le document">
                                    <button type="button" aria-label={`Ouvrir ${title}`} onClick={() => window.open(doc.url, "_blank", "noopener")} style={iconButton}>↓</button>
                                </Tooltip>
                            );
                            const docCell = (
                                <div className="flex items-center gap-3 min-w-0">
                                    <div aria-hidden style={{ width: 40, height: 44, borderRadius: 8, background: DV2.tint, color: primary, display: "grid", placeItems: "center", fontSize: 10, fontWeight: 800, flexShrink: 0 }}>{documentBadge(doc)}</div>
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ fontWeight: 800, fontSize: 15, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</div>
                                        {sub && <div style={{ fontSize: 12, color: DV2.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub}</div>}
                                    </div>
                                </div>
                            );
                            return (
                                <div key={doc.id ?? `${doc.type}-${doc.number ?? i}`} style={{ borderBottom: `1px solid ${DV2.borderSoft}` }}>
                                    <div className="hidden md:grid items-center" style={{ gridTemplateColumns: GRID, padding: "14px 20px", gap: 12, fontSize: 14 }}>
                                        {docCell}
                                        <div>
                                            <div style={{ fontWeight: 700 }}>{person ?? (doc.type === "transport" ? "Document personnel" : "-")}</div>
                                            {parent && <div style={{ fontSize: 12, color: DV2.muted }}>{parent}</div>}
                                        </div>
                                        <div>{documentDateLabel(doc)}</div>
                                        {statusCell}
                                        <div className="flex justify-end">{download}</div>
                                    </div>
                                    <div className="md:hidden flex flex-col gap-2" style={{ padding: "14px 16px" }}>
                                        <div className="flex items-start justify-between gap-2">{docCell}{download}</div>
                                        <div style={{ fontSize: 13, color: DV2.muted }}>{person ? `${person}${parent ? `, ${parent}` : ""}, ` : ""}{documentDateLabel(doc)}</div>
                                        {statusCell}
                                    </div>
                                </div>
                            );
                        })}
                    </section>
                ))}

                {/* Pagination */}
                {!loading && filtered.length > 0 && (
                    <div className="flex flex-wrap items-center justify-between gap-3" style={{ padding: "14px 20px" }}>
                        <span style={{ fontSize: 13, color: DV2.muted }}>{safePage * pageSize + 1} à {Math.min(filtered.length, (safePage + 1) * pageSize)} sur {filtered.length}, les plus récents d'abord</span>
                        <div className="flex items-center gap-2">
                            <label style={{ fontSize: 12, color: DV2.muted }} className="hidden sm:flex items-center gap-2">
                                Par page
                                <select value={pageSize} onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }} style={{ ...fieldStyle(), width: "auto", padding: "4px 8px", fontSize: 12, fontFamily, appearance: "auto" as any }}>
                                    {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
                                </select>
                            </label>
                            <nav aria-label="Pagination" className="flex items-center gap-1">
                                {pages().map((p, i) => p === "…" ? (
                                    <span key={`gap-${i}`} style={{ width: 32, textAlign: "center", color: DV2.placeholder }}>…</span>
                                ) : (
                                    <button key={p} type="button" onClick={() => setPage(p)} aria-current={p === safePage ? "page" : undefined} style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${p === safePage ? primary : DV2.border}`, background: p === safePage ? primary : "#fff", color: p === safePage ? "#fff" : text, fontWeight: 700, fontFamily, cursor: "pointer", fontSize: 13 }}>{p + 1}</button>
                                ))}
                            </nav>
                        </div>
                    </div>
                )}
            </Card>

            <p style={{ fontSize: 12, color: DV2.muted, margin: 0, lineHeight: 1.5 }}>
                {isClient
                    ? "« En vigueur » pour le devis en cours, « Remplacé » quand une nouvelle version existe. Les factures n'ont pas de statut, elles sont émises. L'attestation fiscale annuelle arrive chaque début d'année."
                    : "« En vigueur » pour un devis accepté, « Remplacé » quand une nouvelle version existe, « Valide » pour un abonnement dans sa période. Les factures n'ont pas de statut, elles sont émises."}
            </p>
        </div>
    );
}
